<template>
	<q-card :style="cardStyle">
		<q-toolbar>
			<q-toolbar-title>
				{{ $t(titleNews) }}
			</q-toolbar-title>

			<q-btn flat round dense icon="fal fa-times" v-close-popup />
		</q-toolbar>

		<q-separator />

		<q-card-section class="q-px-xl linkfix">
			<p class="text-h5 title">{{ content.title }}</p>

			<p class="text-weight-thin text-caption q-mb-xl">
				{{
					$dateUtil.fDateText(content.publish_date ? content.publish_date : content.created_date)
				}}
			</p>

			<div class="linkfix" id="news-html" v-html="content.content"></div>
		</q-card-section>

		<q-card-section
			class="bg-grey-2 relative-position"
			v-if="showComments && (comments.length > 0 || loading)"
		>
			<q-list padding>
				<q-item
					v-for="(c, index) in comments"
					:key="index"
					:class="index + 1 === comments.length ? '' : 'q-mb-lg'"
				>
					<q-item-section avatar>
						<q-avatar v-if="c.user_image">
							<img :src="c.user_image" />
						</q-avatar>

						<q-btn
							round
							color="blue-grey-9 no-pointer-events"
							size="md"
							:label="initialUser(c.user_name)"
							v-else
						/>
					</q-item-section>

					<q-item-section>
						<q-item-label lines="1" class="text-weight-medium text-body1">
							{{ c.user_name }}
						</q-item-label>

						<q-item-label caption lines="1">
							{{ $dateUtil.fDateText(c.created_date) }}
						</q-item-label>

						<q-item-label class="text-grey-7">
							{{ c.comment }}
						</q-item-label>
					</q-item-section>
				</q-item>

				<q-item v-if="pagination.totalPages > 1">
					<q-item-section pagination>
						<q-pagination
							class="flex flex-center"
							direction-links
							v-model="pagination.page"
							:max="pagination.totalPages"
							@update:model-value="onChangePage"
						/>
					</q-item-section>
				</q-item>
			</q-list>

			<q-inner-loading :showing="loading">
				<q-spinner-facebook size="1rem" color="text-blue-grey-4" />
			</q-inner-loading>
		</q-card-section>

		<q-separator v-if="showComments" />

		<q-card-section class="q-py-lg bg-grey-2" v-if="showComments">
			<q-list>
				<q-item>
					<q-item-section avatar>
						<q-avatar v-if="user_image">
							<img :src="user_image" />
						</q-avatar>

						<q-btn
							round
							color="blue-grey-9 no-pointer-events"
							size="md"
							:label="labelButton"
							v-else
						/>
					</q-item-section>

					<q-item-section>
						<q-input
							v-model="comment"
							bg-color="white"
							color="blue-grey-4"
							type="text"
							rows="1"
							:placeholder="$t('post_a_comment')"
							dense
							rounded
							outlined
							@keyup.enter.native="postComment()"
						>
							<template v-slot:append>
								<q-btn
									flat
									round
									dense
									icon="fal fa-paper-plane"
									color="blue-grey-4"
									@click="postComment()"
								/>
							</template>
						</q-input>
					</q-item-section>
				</q-item>
			</q-list>
		</q-card-section>
	</q-card>
</template>

<script>
import { mapGetters } from 'vuex'

import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default {
	name: 'NewsDetails',

	props: ['content'],

	setup() {
		return { v$: useVuelidate({ $scope: false }) }
	},

	data() {
		return {
			comment: '',
			loading: false,
			comments: [],
			text: '',
			pagination: {
				page: 1,
				page_size: 5, // Qty comments per page
				totalPages: 1,
			},
		}
	},

	computed: {
		...mapGetters({
			clients: 'params/clients',
			full_name: 'common/full_name',
			id_user_client_selected: 'common/id_user_client_selected',
			user_image: 'common/user_image',
		}),

		labelButton() {
			return this.full_name.charAt(0).toUpperCase()
		},

		cardStyle() {
			return this.$q.platform.is.desktop
				? 'max-width: 80vw; max-height: 80vh; min-width: 80vw;'
				: 'width: 100vw; height: 100vh;'
		},

		titleNews() {
			if (this.id_user_client_selected) {
				const obj = this.clients.find((obj) => obj.id === this.id_user_client_selected)
				if (obj) return obj.title_news === '' ? 'news' : obj.title_news
			}
			return 'news'
		},

		showComments() {
			const obj = this.clients.find((obj) => obj.id === this.id_user_client_selected)
			return obj ? obj.use_comments_news : false
		},
	},

	methods: {
		loadComments() {
			this.loading = true
			const data = {
				page_size: this.pagination.page_size,
				page: this.pagination.page,
			}

			this.$axios
				.get(
					`/etraining_news/news/${this.content.id}/get_user_comments/?${this.$qs.stringify(data)}`
				)
				.then((response) => {
					this.pagination.totalPages =
						response.data.count !== 0
							? Math.ceil(response.data.count / this.pagination.page_size)
							: 1

					this.comments = response.data.results.map((e) => ({ ...e }))
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		onChangePage() {
			this.loadComments()
		},

		initialUser(user_name) {
			return user_name ? user_name.charAt(0).toUpperCase() : ''
		},

		postComment() {
			this.v$.$touch()

			if (!this.v$.$error) {
				this.$q.loading.show()

				let data = {
					comment: this.comment,
				}

				this.$axios
					.post(`/etraining_news/news/${this.content.id}/set_user_comment/`, data)
					.then((response) => {
						this.comment = ''
						this.v$.$reset()
						this.loadComments()
					})
					.catch((e) => {
						console.log(e)
					})
					.finally(() => {
						this.$q.loading.hide()
					})
			}
		},

		fixHTMLImages() {
			const htmlDiv = document.getElementById('news-html')
			if (htmlDiv) {
				const images = htmlDiv.getElementsByTagName('img')
				if (images.length) {
					images.forEach((element) => {
						element.style.maxWidth = '100%'
					})
				}
			}
		},
	},

	mounted() {
		// Don't move the function call below to 'created' as it will not work due to component lifecycle
		this.fixHTMLImages()
	},

	created() {
		this.loadComments()
	},

	validations() {
		return {
			comment: { required },
		}
	},
}
</script>

<style lang="scss" scoped>
.title {
	margin-bottom: 0;
}
</style>
